export type AnalyticsDocument = 'REQUEST' | 'INVOICE'

export interface ApprovalTimes {
  resolved: number
  medianHours: number | null
  onTimePct: number | null
  measured: number
  pending: number
}

export type AgingBucket = 'onTime' | 'dueSoon' | 'overdue' | 'noSla'

export const AGING_BUCKETS: readonly AgingBucket[] = ['onTime', 'dueSoon', 'overdue', 'noSla']

export interface StepAging extends Record<AgingBucket, number> {
  document: AnalyticsDocument
  step: string | null
  title: string | null
  total: number
}

export interface ApprovalAges extends ApprovalTimes, Record<AgingBucket, number> {
  oldestDays: number | null
  breakdown: StepAging[]
}

export interface ApprovalPerson extends ApprovalAges {
  id: number
  name: string
  area: string | null
  society: string | null
  picture: string | null
  isViewer: boolean
  isActive: boolean
}

export type AnalyticsGroupView = 'area' | 'flow' | 'costCenter' | 'society'
export type AnalyticsView = 'person' | AnalyticsGroupView

export interface ApprovalGroup extends ApprovalAges {
  id: string | number | null
  name: string | null
  society: string | null
  document: AnalyticsDocument | null
  people: number[]
}

export interface ApprovalTotals extends ApprovalTimes {
  overdue: number
}

export type TrendGranularity = 'day' | 'week' | 'month'

export interface TrendBar {
  firstDay: string
  lastDay: string
  resolved: number
  measured: number
  onTime: number
  medianHours: number | null
  partial: boolean
  current: boolean
}

export interface ApprovalTrend {
  granularity: TrendGranularity
  bars: TrendBar[]
}

export interface AnalyticsPeriod {
  firstDay: string
  lastDay: string
  days: number
}

export interface AnalyticsSociety {
  id: number
  name: string
}

export interface ApprovalAnalytics {
  documents: AnalyticsDocument[]
  societies: AnalyticsSociety[]
  totals: ApprovalTotals
  people: ApprovalPerson[]
  groups: Record<AnalyticsGroupView, ApprovalGroup[]>
  period: AnalyticsPeriod
  trend: ApprovalTrend
}

export interface ApprovalAnalyticsFilters {
  dateFrom: string | null
  dateTo: string | null
  document: AnalyticsDocument | null
  societies: number[]
  areas: number[]
  costCenters: number[]
  requestTypes: string[]
  policies: number[]
}

export interface AnalyticsFilterOption {
  id: number
  name: string
  code?: string | null
  societyName?: string
}

export interface AnalyticsFilterOptions {
  areas: AnalyticsFilterOption[]
  costCenters: AnalyticsFilterOption[]
  policies: AnalyticsFilterOption[]
  requestTypes: string[]
}

export type AnalyticsExportFormat = 'xlsx' | 'csv'

export interface ApprovalAnalyticsRepository {
  approvals(filters: ApprovalAnalyticsFilters): Promise<ApprovalAnalytics>
  exportUrl(filters: ApprovalAnalyticsFilters, format: AnalyticsExportFormat): Promise<string>
  filterOptions(): Promise<AnalyticsFilterOptions>
}
